import {
    MAX_HISTORY_SIZE,
    SeatingGroupType,
    UNSEATED_GROUP_ID,
} from 'assets/js/pages/campaign-settings/campaign-event/table-seating/constants';
import {
    DropTargetPayload,
    HistorySnapshot,
    SeatingTicket,
    TransformedEventSeating,
    TransformedSeatingGroup,
} from 'assets/js/pages/campaign-settings/campaign-event/table-seating/types';
import {
    canRemoveTable,
    compactSeats,
    getContiguousGroupMembersFromSeat,
    getNextTableNumber,
    getTableOccupancy,
    indexToSeatNumber,
} from 'assets/js/pages/campaign-settings/campaign-event/table-seating/utils/helpers';
import { reshuffleTable } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/utils/reshuffleTable';
import { reshuffleUnseated } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/utils/reshuffleUnseated';
import { cloneDeep, isEqual } from 'lodash';
import { TicketGroup } from 'types/domain';
import { v4 as uuid } from 'uuid';
import { createWithEqualityFn as create } from 'zustand/traditional';

type TableSeatingStateData = {
    tickets: Record<string, SeatingTicket>;
    seatingGroups: Record<string, TransformedSeatingGroup>;
    ticketGroups: Record<string, TicketGroup>;
    originalTickets: Record<string, SeatingTicket>;
    originalSeatingGroups: Record<string, TransformedSeatingGroup>;
    deletedSeatingGroupIds: Set<string>;
    past: HistorySnapshot[];
    future: HistorySnapshot[];
    maxHistorySize: number;
    dragState: {
        activeId: string | null;
        activeData: DropTargetPayload | null;
    };
    selectedItems: Set<string>;
    showCheckboxes: boolean;
    collapsedGroups: Set<string>;
    isInitializing: boolean;
};

interface TableSeatingState extends TableSeatingStateData {
    initializeSeating: (data: TransformedEventSeating) => void;
    preserveUnsavedChanges: (data: TransformedEventSeating) => void;
    seatGuest: (ticketId: string, tableId: string, seatNumber: number) => void;
    unseatGuest: (ticketId: string) => void;
    unseatMultiple: (ticketIds: string[]) => void;
    swapGuests: (ticketId1: string, ticketId2: string) => void;
    reshuffleAndSeat: (
        ticketIds: string | string[],
        tableId: string,
        targetSeatNumber: number,
    ) => void;
    toggleItemSelection: (itemId: string) => void;
    toggleGroupSelection: (groupId: string, tableId: string, startSeatNumber: number) => void;
    clearSelection: () => void;
    toggleGroupCollapse: (groupId: string, tableId: string) => void;
    takeSnapshot: () => void;
    undo: () => void;
    redo: () => void;
    markAsSaved: () => void;
    reset: () => void;
    setDragState: (activeId: string | null, activeData: DropTargetPayload | null) => void;
    updateTableCapacity: (tableId: string, newCapacity: number) => void;
    removeTableSeat: (tableId: string, seatNumber: number) => void;
    addSeatingGroup: (name?: string, capacity?: number) => string;
    removeSeatingGroup: (tableId: string) => void;
}

const initialState: TableSeatingStateData = {
    tickets: {},
    seatingGroups: {},
    ticketGroups: {},
    originalTickets: {},
    originalSeatingGroups: {},
    deletedSeatingGroupIds: new Set<string>(),
    past: [],
    future: [],
    maxHistorySize: MAX_HISTORY_SIZE,
    dragState: {
        activeId: null,
        activeData: null,
    },
    selectedItems: new Set<string>(),
    showCheckboxes: false,
    collapsedGroups: new Set<string>(),
    isInitializing: true,
};

const useTableSeatingStore = create<TableSeatingState>()((set, get) => ({
    ...initialState,
    initializeSeating: (data: TransformedEventSeating) => {
        set({
            tickets: data.tickets,
            seatingGroups: data.seatingGroups,
            ticketGroups: data.ticketGroups,
            originalTickets: cloneDeep(data.tickets),
            originalSeatingGroups: cloneDeep(data.seatingGroups),
            deletedSeatingGroupIds: new Set<string>(),
            past: [],
            future: [],
            isInitializing: false,
        });
    },
    preserveUnsavedChanges: (data: TransformedEventSeating) => {
        set(state => {
            const hasUnsavedChanges =
                !isEqual(state.tickets, state.originalTickets) ||
                !isEqual(state.seatingGroups, state.originalSeatingGroups);

            const dataChanged =
                !isEqual(data.tickets, state.originalTickets) ||
                !isEqual(data.seatingGroups, state.originalSeatingGroups);

            if (!dataChanged && hasUnsavedChanges) {
                return { ...state, ticketGroups: data.ticketGroups };
            }

            return {
                tickets: data.tickets,
                seatingGroups: data.seatingGroups,
                ticketGroups: data.ticketGroups,
                originalTickets: cloneDeep(data.tickets),
                originalSeatingGroups: cloneDeep(data.seatingGroups),
                deletedSeatingGroupIds: new Set<string>(),
                past: [],
                future: [],
                isInitializing: false,
            };
        });
    },
    seatGuest: (ticketId: string, tableId: string, seatNumber: number) => {
        set(state => {
            const ticket = state.tickets[ticketId];
            const originalSeatingGroup = ticket?.seating_group_id;

            const ticketsWithMove = {
                ...state.tickets,
                [ticketId]: {
                    ...state.tickets[ticketId],
                    seating_group_id: tableId,
                    seat_number: seatNumber,
                },
            };

            const compactedTickets =
                originalSeatingGroup === UNSEATED_GROUP_ID
                    ? { ...ticketsWithMove, ...compactSeats(ticketsWithMove, UNSEATED_GROUP_ID) }
                    : ticketsWithMove;

            return { tickets: compactedTickets };
        });
    },
    unseatGuest: (ticketId: string) => {
        set(state => {
            const unseatedCount = Object.values(state.tickets).filter(
                ticket => ticket.seating_group_id === UNSEATED_GROUP_ID,
            ).length;
            const nextSeat = indexToSeatNumber(unseatedCount);

            return {
                tickets: {
                    ...state.tickets,
                    [ticketId]: {
                        ...state.tickets[ticketId],
                        seating_group_id: UNSEATED_GROUP_ID,
                        seat_number: nextSeat,
                    },
                },
            };
        });
    },
    unseatMultiple: (ticketIds: string[]) => {
        set(state => {
            const validTicketIds = ticketIds.filter(
                id => state.tickets[id] && state.tickets[id].seating_group_id !== UNSEATED_GROUP_ID,
            );

            if (validTicketIds.length === 0) return state;

            const currentUnseatedCount = Object.values(state.tickets).filter(
                ticket => ticket.seating_group_id === UNSEATED_GROUP_ID,
            ).length;

            const updates = validTicketIds.reduce(
                (acc, ticketId, index) => {
                    acc[ticketId] = {
                        ...state.tickets[ticketId],
                        seating_group_id: UNSEATED_GROUP_ID,
                        seat_number: indexToSeatNumber(currentUnseatedCount + index),
                    };
                    return acc;
                },
                {} as Record<string, SeatingTicket>,
            );

            return {
                tickets: {
                    ...state.tickets,
                    ...updates,
                },
            };
        });
    },
    swapGuests: (ticketId1: string, ticketId2: string) => {
        set(state => {
            const ticket1 = state.tickets[ticketId1];
            const ticket2 = state.tickets[ticketId2];

            const seat1 = {
                tableId: ticket1?.seating_group_id,
                seatNumber: ticket1?.seat_number,
            };
            const seat2 = {
                tableId: ticket2?.seating_group_id,
                seatNumber: ticket2?.seat_number,
            };

            return {
                tickets: {
                    ...state.tickets,
                    [ticketId1]: {
                        ...state.tickets[ticketId1],
                        seating_group_id: seat2.tableId,
                        seat_number: seat2.seatNumber,
                    },
                    [ticketId2]: {
                        ...state.tickets[ticketId2],
                        seating_group_id: seat1.tableId,
                        seat_number: seat1.seatNumber,
                    },
                },
            };
        });
    },
    reshuffleAndSeat: (
        ticketIds: string | string[],
        tableId: string,
        targetSeatNumber: number,
    ): void => {
        set(state => {
            const incomingTickets = Array.isArray(ticketIds) ? ticketIds : [ticketIds];

            const updates =
                tableId === UNSEATED_GROUP_ID
                    ? reshuffleUnseated(state.tickets, incomingTickets, targetSeatNumber)
                    : reshuffleTable({
                          incomingTicketIds: incomingTickets,
                          tableId,
                          targetSeatNumber,
                          tickets: state.tickets,
                          tableCapacity: state.seatingGroups[tableId]?.capacity || 0,
                      });

            if (Object.keys(updates).length === 0) {
                return state;
            }

            return {
                tickets: {
                    ...state.tickets,
                    ...updates,
                },
                selectedItems: new Set(),
                showCheckboxes: false,
            };
        });
    },
    toggleItemSelection: (itemId: string) => {
        set(state => {
            const newSelection = new Set(state.selectedItems);
            if (newSelection.has(itemId)) {
                newSelection.delete(itemId);
            } else {
                newSelection.add(itemId);
            }
            return {
                selectedItems: newSelection,
                showCheckboxes: newSelection.size > 0,
            };
        });
    },
    toggleGroupSelection: (groupId: string, tableId: string, startSeatNumber: number) => {
        set(state => {
            const newSelection = new Set(state.selectedItems);
            const groupTickets = getContiguousGroupMembersFromSeat(groupId, tableId, {
                startSeatNumber,
                tickets: state.tickets,
            });
            const allSelected = groupTickets.every(id => newSelection.has(id));

            const updatedSelection = allSelected
                ? new Set([...newSelection].filter(id => !groupTickets.includes(id)))
                : new Set([...newSelection, ...groupTickets]);
            return {
                selectedItems: updatedSelection,
                showCheckboxes: updatedSelection.size > 0,
            };
        });
    },
    clearSelection: () => {
        set({
            selectedItems: new Set(),
            showCheckboxes: false,
        });
    },
    toggleGroupCollapse: (groupId: string, tableId: string) => {
        const key = `${groupId}-${tableId}`;
        set(state => {
            const newCollapsed = new Set(state.collapsedGroups);
            if (newCollapsed.has(key)) {
                newCollapsed.delete(key);
            } else {
                newCollapsed.add(key);
            }
            return { collapsedGroups: newCollapsed };
        });
    },
    takeSnapshot: () => {
        const { tickets, seatingGroups, past, maxHistorySize } = get();
        const newSnapshot: HistorySnapshot = {
            tickets: cloneDeep(tickets),
            seatingGroups: cloneDeep(seatingGroups),
        };
        set({
            past: [...past.slice(past.length - maxHistorySize + 1), newSnapshot],
            future: [],
        });
    },
    undo: () => {
        const { past, tickets, seatingGroups } = get();
        const previousState = past[past.length - 1];
        if (previousState) {
            set({
                past: past.slice(0, past.length - 1),
                future: [
                    ...get().future,
                    {
                        tickets: cloneDeep(tickets),
                        seatingGroups: cloneDeep(seatingGroups),
                    },
                ],
                tickets: previousState.tickets,
                seatingGroups: previousState.seatingGroups,
            });
        }
    },
    redo: () => {
        const { past, future, tickets, seatingGroups } = get();
        const nextState = future[future.length - 1];
        if (nextState) {
            set({
                future: future.slice(0, future.length - 1),
                past: [
                    ...past,
                    {
                        tickets: cloneDeep(tickets),
                        seatingGroups: cloneDeep(seatingGroups),
                    },
                ],
                tickets: nextState.tickets,
                seatingGroups: nextState.seatingGroups,
            });
        }
    },
    markAsSaved: () => {
        const { tickets, seatingGroups } = get();
        set({
            originalTickets: cloneDeep(tickets),
            originalSeatingGroups: cloneDeep(seatingGroups),
            deletedSeatingGroupIds: new Set<string>(),
        });
    },
    reset: () => {
        set(initialState);
    },
    setDragState: (activeId: string | null, activeData: DropTargetPayload | null) => {
        set({ dragState: { activeId, activeData } });
    },
    updateTableCapacity: (tableId: string, newCapacity: number) => {
        set(state => {
            const table = state.seatingGroups[tableId];
            if (!table || newCapacity < 1) return state;

            const currentOccupancy = getTableOccupancy(tableId, state.tickets);
            if (newCapacity < currentOccupancy) return state;

            return {
                seatingGroups: {
                    ...state.seatingGroups,
                    [tableId]: {
                        ...state.seatingGroups[tableId],
                        capacity: newCapacity,
                    },
                },
            };
        });
    },
    removeTableSeat: (tableId: string, seatNumber: number) => {
        set(state => {
            const table = state.seatingGroups[tableId];
            if (table?.capacity == null || table.capacity <= 1) return state;

            const seatOccupied = Object.values(state.tickets).some(
                t => t.seating_group_id === tableId && t.seat_number === seatNumber,
            );
            if (seatOccupied) return state;

            const updatedTickets = Object.entries(state.tickets).reduce(
                (acc, [id, ticket]) => {
                    if (
                        ticket.seating_group_id === tableId &&
                        ticket.seat_number != null &&
                        ticket.seat_number > seatNumber
                    ) {
                        acc[id] = { ...ticket, seat_number: ticket.seat_number - 1 };
                    } else {
                        acc[id] = ticket;
                    }
                    return acc;
                },
                {} as Record<string, SeatingTicket>,
            );

            return {
                seatingGroups: {
                    ...state.seatingGroups,
                    [tableId]: {
                        ...state.seatingGroups[tableId],
                        capacity: table.capacity - 1,
                    },
                },
                tickets: updatedTickets,
            };
        });
    },
    addSeatingGroup: (name?: string, capacity: number = 4) => {
        const newTableId = `table-${uuid()}`;

        set(state => {
            const newTableNumber = getNextTableNumber(state.seatingGroups);
            const newTableName = name || `Table ${newTableNumber}`;

            return {
                seatingGroups: {
                    ...state.seatingGroups,
                    [newTableId]: {
                        id: newTableId,
                        name: newTableName,
                        capacity,
                        number: newTableNumber,
                        locked: false,
                        type: SeatingGroupType.TABLE,
                    },
                },
            };
        });

        return newTableId;
    },
    removeSeatingGroup: (tableId: string) => {
        set(state => {
            if (!canRemoveTable(tableId, state.seatingGroups, state.tickets)) {
                return state;
            }

            const newSeatingGroups = { ...state.seatingGroups };
            delete newSeatingGroups[tableId];

            const newDeletedIds = new Set(state.deletedSeatingGroupIds);
            if (!tableId.startsWith('table-')) {
                newDeletedIds.add(tableId);
            }

            return {
                seatingGroups: newSeatingGroups,
                deletedSeatingGroupIds: newDeletedIds,
            };
        });
    },
}));

export { useTableSeatingStore };
